import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Paper, Select, TextField,
  Table, TableBody, TableCell, TableHead, TableRow, Typography,
  makeStyles
} from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
  content: { padding: theme.spacing(2), overflowY: "auto" },
  status: { padding: theme.spacing(3), marginBottom: theme.spacing(2), textAlign: "center" },
  clock: { fontSize: 34, fontWeight: 700, margin: theme.spacing(1) },
  filters: { display: "flex", gap: 12, marginBottom: theme.spacing(2), flexWrap: "wrap" },
  filter: { minWidth: 220 },
  action: { minWidth: 220, minHeight: 48 },
  summary: { marginTop: theme.spacing(1), color: theme.palette.text.secondary }
}));

const labels = {
  clock_in: "Registrar entrada",
  break_start: "Iniciar intervalo",
  break_end: "Finalizar intervalo",
  clock_out: "Registrar saída"
};

const statusLabels = {
  clock_in: "Expediente ainda não iniciado",
  break_start: "Trabalhando",
  break_end: "Em intervalo",
  clock_out: "Trabalhando após o intervalo"
};

const dayKey = value => new Date(value).toLocaleDateString("pt-BR");
const time = value => new Date(value).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const workedMinutes = punches => {
  const ordered = [...punches].sort((a, b) => new Date(a.punchedAt) - new Date(b.punchedAt));
  let started = null;
  let total = 0;
  ordered.forEach(item => {
    if (item.type === "clock_in" || item.type === "break_end") started = new Date(item.punchedAt);
    if ((item.type === "break_start" || item.type === "clock_out") && started) {
      total += new Date(item.punchedAt) - started;
      started = null;
    }
  });
  if (started && dayKey(started) === dayKey(new Date())) total += new Date() - started;
  return Math.max(0, Math.floor(total / 60000));
};

const duration = minutes => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;

const Attendance = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [now, setNow] = useState(new Date());
  const [today, setToday] = useState([]);
  const [nextType, setNextType] = useState("clock_in");
  const [history, setHistory] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(user.id);
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDayPunches, setEditDayPunches] = useState([]);
  const [editPunchId, setEditPunchId] = useState("");
  const [editType, setEditType] = useState("clock_in");
  const [editAt, setEditAt] = useState("");
  const [editNote, setEditNote] = useState("");

  const loadStatus = async () => {
    const { data } = await api.get("/attendance/status");
    setToday(data.punches);
    setNextType(data.nextType);
  };

  const loadHistory = async () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    const { data } = await api.get("/attendance", {
      params: { userId: selectedUser, from: from.toISOString(), to: to.toISOString() }
    });
    setHistory(data);
  };

  useEffect(() => {
    Promise.all([
      loadStatus(),
      user.profile === "admin" ? api.get("/users") : Promise.resolve(null)
    ]).then(([, result]) => {
      if (result) setUsers(result.data.users);
    }).catch(toastError);
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => { loadHistory().catch(toastError); }, [selectedUser]);

  const grouped = useMemo(() => history.reduce((acc, item) => {
    const key = dayKey(item.punchedAt);
    acc[key] = [...(acc[key] || []), item];
    return acc;
  }, {}), [history]);

  const punch = async () => {
    setLoading(true);
    try {
      await api.post("/attendance/punch", { type: nextType });
      await Promise.all([loadStatus(), loadHistory()]);
    } catch (err) { toastError(err); }
    finally { setLoading(false); }
  };

  const choosePunch = (punches, punchId) => {
    const selected = punches.find(item => Number(item.id) === Number(punchId));
    if (!selected) return;
    setEditPunchId(selected.id); setEditType(selected.type);
    setEditAt(new Date(selected.punchedAt).toISOString().slice(0, 16));
  };
  const openAdjustment = punches => {
    setEditDayPunches(punches); setEditNote("");
    choosePunch(punches, punches[0]?.id); setEditOpen(true);
  };
  const saveAdjustment = async () => {
    if (!editPunchId || editNote.trim().length < 3) return;
    try {
      await api.put(`/attendance/${editPunchId}`, { type: editType, punchedAt: new Date(editAt).toISOString(), note: editNote });
      setEditOpen(false); await loadHistory();
    } catch (err) { toastError(err); }
  };
  const exportCsv = () => {
    const rows = [["Data", "Entrada", "Início do intervalo", "Fim do intervalo", "Saída", "Total"]];
    Object.entries(grouped).forEach(([date, punches]) => {
      const byType = punches.reduce((acc, item) => ({ ...acc, [item.type]: item }), {});
      rows.push([date, byType.clock_in ? time(byType.clock_in.punchedAt) : "", byType.break_start ? time(byType.break_start.punchedAt) : "", byType.break_end ? time(byType.break_end.punchedAt) : "", byType.clock_out ? time(byType.clock_out.punchedAt) : "", duration(workedMinutes(punches))]);
    });
    const csv = `\uFEFF${rows.map(row => row.map(value => `"${String(value).replace(/"/g, '""')}"`).join(";")).join("\r\n")}`;
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    link.download = `bate-ponto-${selectedUser}.csv`; link.click(); URL.revokeObjectURL(link.href);
  };

  return <MainContainer>
    <MainHeader><Title>Bate-ponto</Title></MainHeader>
    <div className={classes.content}>
      <Paper className={classes.status} variant="outlined">
        <AccessTimeIcon color="primary" fontSize="large" />
        <Typography className={classes.clock}>{now.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</Typography>
        <Typography>{statusLabels[nextType]}</Typography>
        <Typography className={classes.summary}>Total de hoje: {duration(workedMinutes(today))}</Typography>
        <Button className={classes.action} variant="contained" color="primary" disabled={loading} onClick={punch}>
          {labels[nextType]}
        </Button>
      </Paper>

      <div className={classes.filters}>
        {user.profile === "admin" && <FormControl variant="outlined" size="small" className={classes.filter}>
          <InputLabel>Atendente</InputLabel>
          <Select value={selectedUser} onChange={event => setSelectedUser(event.target.value)} label="Atendente">
            {users.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
          </Select>
        </FormControl>}
        {user.profile === "admin" && <Button variant="outlined" color="primary" onClick={exportCsv}>Exportar CSV</Button>}
      </div>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead><TableRow><TableCell>Data</TableCell><TableCell>Entrada</TableCell><TableCell>Início do intervalo</TableCell><TableCell>Fim do intervalo</TableCell><TableCell>Saída</TableCell><TableCell>Total</TableCell>{user.profile === "admin" && <TableCell>Ações</TableCell>}</TableRow></TableHead>
          <TableBody>{Object.entries(grouped).map(([date, punches]) => {
            const byType = punches.reduce((acc, item) => ({ ...acc, [item.type]: item }), {});
            return <TableRow key={date}>
              <TableCell>{date}</TableCell>
              <TableCell>{byType.clock_in ? time(byType.clock_in.punchedAt) : "—"}</TableCell>
              <TableCell>{byType.break_start ? time(byType.break_start.punchedAt) : "—"}</TableCell>
              <TableCell>{byType.break_end ? time(byType.break_end.punchedAt) : "—"}</TableCell>
              <TableCell>{byType.clock_out ? time(byType.clock_out.punchedAt) : "—"}</TableCell>
              <TableCell>{duration(workedMinutes(punches))}</TableCell>
              {user.profile === "admin" && <TableCell><Button size="small" onClick={() => openAdjustment(punches)}>Ajustar</Button></TableCell>}
            </TableRow>;
          })}</TableBody>
        </Table>
      </Paper>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ajustar marcação</DialogTitle><DialogContent dividers>
          <FormControl fullWidth margin="dense"><InputLabel>Marcação</InputLabel><Select value={editPunchId} onChange={event => choosePunch(editDayPunches, event.target.value)}>{editDayPunches.map(item => <MenuItem key={item.id} value={item.id}>{labels[item.type]} — {time(item.punchedAt)}</MenuItem>)}</Select></FormControl>
          <FormControl fullWidth margin="dense"><InputLabel>Tipo</InputLabel><Select value={editType} onChange={event => setEditType(event.target.value)}>{Object.entries(labels).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}</Select></FormControl>
          <TextField fullWidth margin="dense" type="datetime-local" label="Data e horário" value={editAt} onChange={event => setEditAt(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField fullWidth margin="dense" multiline rows={3} variant="outlined" label="Justificativa obrigatória" value={editNote} onChange={event => setEditNote(event.target.value)} />
        </DialogContent><DialogActions><Button onClick={() => setEditOpen(false)}>Cancelar</Button><Button color="primary" variant="contained" disabled={editNote.trim().length < 3} onClick={saveAdjustment}>Salvar ajuste</Button></DialogActions>
      </Dialog>
    </div>
  </MainContainer>;
};

export default Attendance;
